import axios from 'axios';
import { useContext } from 'react';
import { useHistory } from 'react-router';
import config from '../config';
import AuthContext from '../store/auth-context';

const api = axios.create({ baseURL: config.apiURL });

let lastPath = '/';

const refreshAccessToken = async (history, auth, retryConfig) => {
  lastPath = history.location.pathname;

  // Get a new access token since old one is bad.
  const tokenFetcher = axios.create({ baseURL: config.apiURL, validateStatus: null });
  const response = await tokenFetcher.post('/users/accessToken');

  // Session Expired, redirect to login.
  if (response.status === 401) {
    history.push(`/?redirect=${lastPath}`);
    auth.clearAccessToken();
    throw new Error(response.data);

    // Use new token to re-dispatch the request.
  } else if (response.status === 200) {
    const newToken = response.headers['x-access-token'];
    auth.login(newToken, response.data.exp);
    retryConfig.headers['x-access-token'] = newToken;
    return axios(retryConfig);

    // Other system error when getting new token.
  } else {
    throw new Error(
      response?.data ?? `Login process failed with HTTP response code ${response.status}.`
    );
  }
};

export const useAPI = () => {
  const history = useHistory();
  const auth = useContext(AuthContext);

  api.defaults.headers.common['x-access-token'] = auth.token;

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        if (history.location.pathname !== '/login') {
          return refreshAccessToken(history, auth, error.response.config);
        } else {
          throw new Error(error.response?.data ?? 'Unable to log in to the requested account.');
        }
      } else {
        throw error;
      }
    }
  );

  return api;
};

// Only to be used where both the auth and router
// contexts are not available.
export const rawAPI = axios.create({ baseURL: config.apiURL });
